const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Generic API functions
const apiRequest = async (endpoint, options = {}) => {
  try {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;

      if (response.status === 403 && typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('suspend')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.setItem('userLogoutMessage', errorMessage);
        window.dispatchEvent(new CustomEvent('auth:logout'));
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new Error(errorMessage);
      }

      if (response.status === 403) {
        const path = window.location.pathname;
        if (!path.includes('/payment-required') && !path.includes('/subscription-expired') && !path.includes('/payment/result')) {
          const to = '/payment-required';
          window.location.href = to;
        }
        throw new Error(errorData.message || errorData.error || 'Subscription required');
      }
      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    // Don't log expected 404s for pricing endpoints
    const isPricing404 = endpoint.includes('/pricing/') && 
                        (error.message.includes('Pricing data not found') || 
                         error.message.includes('404'));
    
    // Don't log expected constraint violation errors (these are handled gracefully by the UI)
    const isConstraintViolation = error.message.includes('Cannot delete') && 
                                 (error.message.includes('being used as ingredient in other recipes') || 
                                  error.message.includes('Cannot delete material') ||
                                  error.message.includes('Cannot delete category'));
    
    if (!isPricing404 && !isConstraintViolation) {
      console.error('API request failed:', error);
    }
    throw error;
  } finally {
    // Reset idle timeout on any API call (successful or failed)
    // This indicates user activity
    window.dispatchEvent(new CustomEvent('auth:activity'));
  }
};

// Silent API request for pricing endpoints (no console logging)
const silentApiRequest = async (endpoint, options = {}) => {
  try {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 403) {
        const path = window.location.pathname;
        if (!path.includes('/payment-required') && !path.includes('/subscription-expired') && !path.includes('/payment/result')) {
          const to = '/payment-required';
          window.location.href = to;
        }
        throw new Error(errorData.message || errorData.error || 'Subscription required');
      }
      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Check if this is a "no pricing data" response
    if (data.exists === false) {
      return null;
    }
    
    return data;
  } catch (error) {
    throw error;
  } finally {
    // Reset idle timeout on any API call (successful or failed)
    // This indicates user activity
    window.dispatchEvent(new CustomEvent('auth:activity'));
  }
};

// Materials API
export const materialsAPI = {
  // Get all materials
  getAll: () => apiRequest('/materials'),
  
  // Get materials by category
  getByCategory: (categoryId) => apiRequest(`/materials/category/${categoryId}`),
  
  // Get single material
  getById: (id) => apiRequest(`/materials/${id}`),
  
  // Create material
  create: (materialData) => {
    console.log('API create material data:', materialData);
    return apiRequest('/materials', {
      method: 'POST',
      body: JSON.stringify(materialData),
    });
  },
  
  // Update material
  update: (id, materialData) => {
    console.log('API update material data:', materialData);
    return apiRequest(`/materials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(materialData),
    });
  },
  
  // Delete material
  delete: (id) => apiRequest(`/materials/${id}`, {
    method: 'DELETE',
  }),
  
  // Material categories
  getCategories: () => apiRequest('/materials/categories'),
  createCategory: (categoryData) => apiRequest('/materials/categories', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  }),
  updateCategory: (id, categoryData) => apiRequest(`/materials/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData),
  }),
  deleteCategory: (id) => apiRequest(`/materials/categories/${id}`, {
    method: 'DELETE',
  }),
  
  // Get usage statistics for a material
  getUsageStats: (materialId) => apiRequest(`/materials/${materialId}/usage-stats`),

  // Material IDs used in at least one dish
  getRecipeUsageIds: () => apiRequest('/materials/recipe-usage-ids'),
};

// Recipes API
export const recipesAPI = {
  // Get all recipes
  getAll: () => apiRequest('/recipes'),
  
  // Get recipes by category
  getByCategory: (categoryId) => apiRequest(`/recipes/category/${categoryId}`),
  
  // Get single recipe with ingredients
  getById: (id) => apiRequest(`/recipes/${id}`),
  
  // Create recipe
  create: (recipeData) => apiRequest('/recipes', {
    method: 'POST',
    body: JSON.stringify(recipeData),
  }),
  
  // Update recipe
  update: (id, recipeData) => apiRequest(`/recipes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(recipeData),
  }),
  
  // Delete recipe
  delete: (id) => apiRequest(`/recipes/${id}`, {
    method: 'DELETE',
  }),

  // Duplicate recipe
  duplicate: (id) => apiRequest(`/recipes/${id}/duplicate`, {
    method: 'POST',
  }),
  
  // Recipe categories
  getCategories: () => apiRequest('/recipes/categories'),
  createCategory: (categoryData) => apiRequest('/recipes/categories', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  }),
  updateCategory: (id, categoryData) => apiRequest(`/recipes/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData),
  }),
  deleteCategory: (id) => apiRequest(`/recipes/categories/${id}`, {
    method: 'DELETE',
  }),
};

// Dashboard API
export const dashboardAPI = {
  // Get dashboard statistics
  getStats: () => apiRequest('/dashboard/stats'),
  
  // Get recipe cost breakdown
  getRecipeCost: (id) => apiRequest(`/dashboard/recipe-cost/${id}`),
  
  // Get material usage statistics
  getMaterialUsage: () => apiRequest('/dashboard/material-usage'),
  
  // Get cost trends
  getCostTrends: () => apiRequest('/dashboard/cost-trends'),
  
  // Get break-even analysis
  getBreakEven: () => apiRequest('/dashboard/break-even'),
};

// Upload API
export const uploadAPI = {
  // Upload image
  uploadImage: async (file, signal, folder = 'materials') => {
    try {
      const token = getAuthToken();
      console.log('Upload request - Token:', token ? 'present' : 'missing');
      console.log('Upload request - File:', file.name, file.size, file.type);
      console.log('Upload request - Folder:', folder);
      
      const formData = new FormData();
      formData.append('image', file);

      const uploadUrl = `${API_BASE_URL}/upload/image?folder=${folder}`;
      console.log('Upload URL:', uploadUrl);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
        signal: signal, // Add abort signal support
      });

      console.log('Upload response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('Upload error response:', errorData);
        if (response.status === 403) {
          const path = window.location.pathname;
          if (!path.includes('/payment-required') && !path.includes('/subscription-expired') && !path.includes('/payment/result')) {
            const to = '/payment-required';
            window.location.href = to;
          }
          throw new Error(errorData.message || errorData.error || 'Subscription required');
        }
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        const errorMessage = errorData.details || errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Upload success:', result);
      return result;
    } catch (error) {
      console.error('Upload request failed:', error);
      throw error;
    } finally {
      // Reset idle timeout on any API call (successful or failed)
      // This indicates user activity
      window.dispatchEvent(new CustomEvent('auth:activity'));
    }
  },

  // Delete image
  deleteImage: (publicId) => {
    // URL-encode the publicId to handle slashes in the path (e.g., "user_schema/logos/image")
    const encodedPublicId = encodeURIComponent(publicId);
    return apiRequest(`/upload/image/${encodedPublicId}`, {
      method: 'DELETE',
    });
  },
};

// Allocated Fixed Costs API
export const fixedOverheadAPI = {
      // Get all allocated fixed cost items
  getItems: () => apiRequest('/fixed-overhead/items'),
  
      // Create allocated fixed cost item
  createItem: (itemData) => apiRequest('/fixed-overhead/items', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),
  
      // Update allocated fixed cost item
  updateItem: (id, itemData) => apiRequest(`/fixed-overhead/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  }),
  
      // Delete allocated fixed cost item
  deleteItem: (id) => apiRequest(`/fixed-overhead/items/${id}`, {
    method: 'DELETE',
  }),
  
  // Get total allocated fixed costs
  getTotal: () => apiRequest('/fixed-overhead/total'),
  
  // Get recipe categories for distribution
  getCategories: () => apiRequest('/fixed-overhead/categories'),
  
      // Get allocated fixed cost distribution
  getDistribution: () => apiRequest('/fixed-overhead/distribution'),
  
      // Create allocated fixed cost distribution
  createDistribution: (distributionData) => apiRequest('/fixed-overhead/distribution', {
    method: 'POST',
    body: JSON.stringify(distributionData),
  }),
  
      // Update allocated fixed cost distribution
  updateDistribution: (id, distributionData) => apiRequest(`/fixed-overhead/distribution/${id}`, {
    method: 'PUT',
    body: JSON.stringify(distributionData),
  }),
  
      // Update allocated fixed cost distribution by category_id
  updateDistributionByCategory: (categoryId, distributionData) => apiRequest(`/fixed-overhead/distribution/category/${categoryId}`, {
    method: 'PUT',
    body: JSON.stringify(distributionData),
  }),
  
      // Delete allocated fixed cost distribution
  deleteDistribution: (id) => apiRequest(`/fixed-overhead/distribution/${id}`, {
    method: 'DELETE',
  }),
  
  // Get distribution summary with calculations
  getDistributionSummary: () => apiRequest('/fixed-overhead/distribution/summary'),
  
  // Initialize distribution for all categories
  initializeDistribution: () => apiRequest('/fixed-overhead/distribution/initialize', {
    method: 'POST',
  }),
};

// Allocated Variable Costs API
export const variableOverheadAPI = {
  // Get all allocated variable cost items
  getItems: () => apiRequest('/variable-overhead/items'),
  
  // Create allocated variable cost item
  createItem: (itemData) => apiRequest('/variable-overhead/items', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),
  
  // Update allocated variable cost item
  updateItem: (id, itemData) => apiRequest(`/variable-overhead/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  }),
  
  // Delete allocated variable cost item
  deleteItem: (id) => apiRequest(`/variable-overhead/items/${id}`, {
    method: 'DELETE',
  }),
  
  // Get total allocated variable costs
  getTotal: () => apiRequest('/variable-overhead/total'),
  
  // Get recipe categories for distribution
  getCategories: () => apiRequest('/variable-overhead/categories'),
  
  // Get allocated variable cost distribution
  getDistribution: () => apiRequest('/variable-overhead/distribution'),
  
  // Create allocated variable cost distribution
  createDistribution: (distributionData) => apiRequest('/variable-overhead/distribution', {
    method: 'POST',
    body: JSON.stringify(distributionData),
  }),
  
  // Update allocated variable cost distribution
  updateDistribution: (id, distributionData) => apiRequest(`/variable-overhead/distribution/${id}`, {
    method: 'PUT',
    body: JSON.stringify(distributionData),
  }),
  
  // Update allocated variable cost distribution by category_id
  updateDistributionByCategory: (categoryId, distributionData) => apiRequest(`/variable-overhead/distribution/category/${categoryId}`, {
    method: 'PUT',
    body: JSON.stringify(distributionData),
  }),
  
  // Delete allocated variable cost distribution
  deleteDistribution: (id) => apiRequest(`/variable-overhead/distribution/${id}`, {
    method: 'DELETE',
  }),
  
  // Get distribution summary with calculations
  getDistributionSummary: () => apiRequest('/variable-overhead/distribution/summary'),
  
  // Initialize distribution for all categories
  initializeDistribution: () => apiRequest('/variable-overhead/distribution/initialize', {
    method: 'POST',
  }),
};

// Pricing API
export const pricingAPI = {
  // Get pricing data for a recipe
  getPricing: (recipeId) => silentApiRequest(`/pricing/${recipeId}`),
  
  // Save pricing data for a recipe
  savePricing: (recipeId, pricingData) => apiRequest(`/pricing/${recipeId}`, {
    method: 'POST',
    body: JSON.stringify(pricingData),
  }),
  
  // Update pricing data for a recipe
  updatePricing: (recipeId, pricingData) => apiRequest(`/pricing/${recipeId}`, {
    method: 'PUT',
    body: JSON.stringify(pricingData),
  }),
  
  // Get costing defaults for a recipe (raw material, fixed, variable costs)
  getCostingDefaults: (recipeId) => apiRequest(`/pricing/costing-defaults/${recipeId}`),
  
  // Get all pricing records
  getAllPricing: () => apiRequest('/pricing'),
  
  // Delete pricing record
  deletePricing: (recipeId) => apiRequest(`/pricing/${recipeId}`, {
    method: 'DELETE',
  }),
  
  // ===== PRICING APPLICATIONS =====
  
  // Get all pricing applications
  getApplications: () => apiRequest('/pricing/applications'),
  
  // Get active pricing applications
  getActiveApplications: () => apiRequest('/pricing/applications/active'),
  
  // Create new pricing application
  createApplication: (applicationData) => apiRequest('/pricing/applications', {
    method: 'POST',
    body: JSON.stringify(applicationData),
  }),
  
  // Update pricing application
  updateApplication: (id, applicationData) => apiRequest(`/pricing/applications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(applicationData),
  }),
  
  // Delete pricing application
  deleteApplication: (id) => apiRequest(`/pricing/applications/${id}`, {
    method: 'DELETE',
  }),
  
  // Toggle pricing application active status
  toggleApplication: (id) => apiRequest(`/pricing/applications/${id}/toggle`, {
    method: 'PATCH',
  }),
};

// User Auth API
export const authAPI = {
  forgotPassword: (email) => apiRequest('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  validateResetToken: (token) => apiRequest('/auth/reset-password/validate', {
    method: 'POST',
    body: JSON.stringify({ token }),
  }),
  resetPassword: (token, newPassword) => apiRequest('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  }),
};

// Affiliate Auth API
const getAffiliateAuthToken = () => {
  return localStorage.getItem('affiliateToken');
};

const getAffiliateToken = () => {
  return localStorage.getItem('affiliateToken');
};

const affiliateApiRequest = async (endpoint, options = {}) => {
  try {
    const token = getAffiliateAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const serverMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;

      // Force logout and notify affiliate if account becomes suspended during an active session.
      if (
        token &&
        response.status === 403 &&
        typeof serverMessage === 'string' &&
        serverMessage.toLowerCase().includes('suspend')
      ) {
        localStorage.removeItem('affiliateToken');
        localStorage.removeItem('affiliate');
        localStorage.setItem('affiliateLogoutMessage', serverMessage);
        window.dispatchEvent(new CustomEvent('affiliate:logout'));
        if (!window.location.pathname.includes('/affiliate/login')) {
          window.location.href = '/affiliate/login';
        }
      }

      if (response.status === 401) {
        localStorage.removeItem('affiliateToken');
        localStorage.removeItem('affiliate');
        window.dispatchEvent(new CustomEvent('affiliate:logout'));
        if (!window.location.pathname.includes('/affiliate/login')) {
          window.location.href = '/affiliate/login';
        }
      }

      throw new Error(serverMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Affiliate API request failed:', error);
    throw error;
  }
};

export const affiliateAuthAPI = {
  register: (username, email, phone, country, password) => {
    return affiliateApiRequest('/affiliates/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, phone, country, password }),
    });
  },
  
  login: (email, password) => {
    return affiliateApiRequest('/affiliates/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  
  getMe: () => {
    return affiliateApiRequest('/affiliates/auth/me');
  },
  
  verifyEmail: (token) => {
    return affiliateApiRequest(`/affiliates/auth/verify-email?token=${token}`, {
      method: 'GET',
    });
  },
  
  resendVerification: (affiliateId) => {
    return affiliateApiRequest('/affiliates/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ affiliateId }),
    });
  },

  forgotPassword: (email) => {
    return affiliateApiRequest('/affiliates/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  validateResetToken: (token) => {
    return affiliateApiRequest('/affiliates/auth/reset-password/validate', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  resetPassword: (token, newPassword) => {
    return affiliateApiRequest('/affiliates/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  },
};

// Affiliate Dashboard API
export const affiliateDashboardAPI = {
  getStats: (month = 'all') => {
    return affiliateApiRequest(`/affiliates/dashboard/stats?month=${encodeURIComponent(month)}`);
  },
  
  getReferrals: (month = 'all') => {
    return affiliateApiRequest(`/affiliates/referrals?month=${encodeURIComponent(month)}`);
  },
  
  getCommissions: (month = 'all') => {
    return affiliateApiRequest(`/affiliates/commissions?month=${encodeURIComponent(month)}`);
  },
  
  getReferralCode: () => {
    return affiliateApiRequest('/affiliates/referral-code');
  },

  getPayoutDetails: () => {
    return affiliateApiRequest('/affiliates/payout-details');
  },

  updatePayoutDetails: (preferredPaymentMethod, payoutDetails) => {
    return affiliateApiRequest('/affiliates/payout-details', {
      method: 'PUT',
      body: JSON.stringify({ preferredPaymentMethod, payoutDetails }),
    });
  },
};

// Health check
export const healthCheck = () => apiRequest('/health');

export default {
  materials: materialsAPI,
  recipes: recipesAPI,
  dashboard: dashboardAPI,
  upload: uploadAPI,
  fixedOverhead: fixedOverheadAPI,
  variableOverhead: variableOverheadAPI,
  pricing: pricingAPI,
  auth: authAPI,
  affiliateAuth: affiliateAuthAPI,
  affiliateDashboard: affiliateDashboardAPI,
  health: healthCheck,
}; 